import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  IconButton,
  Chip,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  companiesService,
  Company,
  listsService,
  List,
  productsService,
  Product,
} from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';

type CompanyListsMap = { [productId: string]: string[] };

const CompanyListsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('empresas', 'editar');
  const { id } = useParams<{ id: string }>();
  const [company, setCompany] = useState<Company | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [allLists, setAllLists] = useState<List[]>([]);
  const [listsMap, setListsMap] = useState<CompanyListsMap>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [comp, products, lists] = await Promise.all([
          companiesService.getById(id),
          productsService.getAll(),
          listsService.getAll(),
        ]);
        if (!cancelled) {
          setCompany(comp);
          setAllProducts(products);
          setAllLists(lists);
          const initialMap: CompanyListsMap = {};
          Object.entries(comp.lists || {}).forEach(([productId, listIds]) => {
            initialMap[productId] = [...(listIds || [])];
          });
          setListsMap(initialMap);
        }
      } catch (error) {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: error instanceof Error ? error.message : 'Erro ao carregar dados',
            severity: 'error',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const listsById = useMemo(() => {
    const m = new Map<string, List>();
    allLists.forEach((l) => m.set(l.id, l));
    return m;
  }, [allLists]);

  const productsById = useMemo(() => {
    const m = new Map<string, Product>();
    allProducts.forEach((p) => m.set(p.id, p));
    return m;
  }, [allProducts]);

  const listsByProduct = useMemo(() => {
    const m = new Map<string, List[]>();
    allLists.forEach((l) => {
      if (!l.productId) return;
      const arr = m.get(l.productId) || [];
      arr.push(l);
      m.set(l.productId, arr);
    });
    return m;
  }, [allLists]);

  const productsNotInMap = useMemo(
    () => allProducts.filter((p) => !(p.id in listsMap)),
    [allProducts, listsMap]
  );

  const entries = useMemo(() => Object.entries(listsMap), [listsMap]);

  const addProduct = (productId: string) => {
    if (productId in listsMap) return;
    setListsMap((prev) => ({ ...prev, [productId]: [] }));
  };

  const removeProduct = (productId: string) => {
    setListsMap((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const addList = (productId: string, listId: string) => {
    setListsMap((prev) => {
      const current = prev[productId] || [];
      if (current.includes(listId)) return prev;
      return { ...prev, [productId]: [...current, listId] };
    });
  };

  const removeList = (productId: string, index: number) => {
    setListsMap((prev) => ({
      ...prev,
      [productId]: (prev[productId] || []).filter((_, i) => i !== index),
    }));
  };

  const moveList = (productId: string, from: number, to: number) => {
    setListsMap((prev) => {
      const current = [...(prev[productId] || [])];
      if (to < 0 || to >= current.length) return prev;
      const [item] = current.splice(from, 1);
      current.splice(to, 0, item);
      return { ...prev, [productId]: current };
    });
  };

  const handleSave = async () => {
    if (!id) return;
    try {
      setSubmitting(true);
      const invalid: string[] = [];
      Object.entries(listsMap).forEach(([productId, listIds]) => {
        listIds.forEach((lid) => {
          const list = listsById.get(lid);
          if (!list) invalid.push(`${lid} (não encontrada)`);
          else if (list.productId !== productId) invalid.push(`${lid} (não pertence a ${productId})`);
        });
      });
      if (invalid.length > 0) {
        throw new Error(`Corrija as listas inválidas antes de salvar: ${invalid.join(', ')}`);
      }

      const payloadMap: CompanyListsMap = {};
      Object.entries(listsMap).forEach(([productId, listIds]) => {
        if (listIds.length > 0) payloadMap[productId] = listIds;
      });
      const result = await companiesService.setLists(id, payloadMap);
      if (!result.success) {
        throw new Error(result.error || 'Falha ao salvar o mapa de listas');
      }
      setSnackbar({ open: true, message: 'Listas salvas com sucesso!', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Erro ao salvar listas',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!company) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/companies')}>
              Voltar
            </Button>
          }
        >
          Empresa não encontrada.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/companies')} variant="text" />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Listas — {company?.name}
          </Typography>
        </Box>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Empresas.'}>
          <span>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canManage || submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {submitting ? 'Salvando…' : 'Salvar'}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Listas de acesso por produto
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        A ordem das listas em cada produto define a prioridade de acesso. Use as setas para reordenar.
      </Typography>

      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Nenhum produto configurado. Adicione um produto abaixo para definir quais listas esta empresa acessa.
        </Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {entries.map(([productId, listIds]) => {
            const product = productsById.get(productId);
            const available = (listsByProduct.get(productId) || []).filter(
              (l) => !listIds.includes(l.id)
            );
            return (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={productId}>
                <Paper
                  variant="outlined"
                  sx={{ display: 'flex', flexDirection: 'column', p: 1.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', minWidth: 0 }}>
                      <Tooltip title={product ? (product.nomeComercial || product.titulo) : productId}>
                        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                          {product ? (product.nomeComercial || product.titulo) : productId}
                        </Typography>
                      </Tooltip>
                      {product ? (
                        !product.isActive && (
                          <Chip label="produto inativo" size="small" color="warning" variant="outlined" />
                        )
                      ) : (
                        <Chip label="produto não encontrado" size="small" color="error" variant="outlined" />
                      )}
                    </Box>
                    <Tooltip title={canManage ? 'Remover produto' : 'Você não tem permissão de edição no módulo Empresas.'}>
                      <span>
                        <IconButton aria-label="Remover produto" size="small" color="error" onClick={() => removeProduct(productId)} disabled={!canManage || submitting}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  <Box sx={{ minHeight: 220, maxHeight: 280, overflowY: 'auto' }}>
                    {listIds.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Nenhuma lista vinculada a este produto.
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {listIds.map((lid, i) => {
                          const list = listsById.get(lid);
                          const belongs = list ? list.productId === productId : false;
                          return (
                            <Box key={`${lid}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip label={`${i + 1}`} size="small" />
                              <Tooltip title={list ? list.name : `${lid} (não encontrada)`}>
                                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                  {list ? list.name : `${lid} (não encontrada)`}
                                </Typography>
                              </Tooltip>
                              {list && !belongs && (
                                <Chip label="não pertence a este produto" size="small" color="error" variant="outlined" />
                              )}
                              {!list && (
                                <Chip label="órfã" size="small" color="error" variant="outlined" />
                              )}
                              <Tooltip title={canManage ? 'Subir' : 'Você não tem permissão de edição no módulo Empresas.'}>
                                <span>
                                  <IconButton aria-label="Subir" size="small" onClick={() => moveList(productId, i, i - 1)} disabled={!canManage || submitting || i === 0}>
                                    <ArrowUpwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={canManage ? 'Descer' : 'Você não tem permissão de edição no módulo Empresas.'}>
                                <span>
                                  <IconButton aria-label="Descer" size="small" onClick={() => moveList(productId, i, i + 1)} disabled={!canManage || submitting || i === listIds.length - 1}>
                                    <ArrowDownwardIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={canManage ? 'Remover lista' : 'Você não tem permissão de edição no módulo Empresas.'}>
                                <span>
                                  <IconButton aria-label="Remover lista" size="small" color="error" onClick={() => removeList(productId, i)} disabled={!canManage || submitting}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>

                  <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Empresas.'}>
                    <span style={{ display: 'block', width: '100%' }}>
                      <Autocomplete<List, false, false, false>
                        options={available}
                        value={null}
                        blurOnSelect
                        disabled={!canManage || submitting}
                        fullWidth
                        onChange={(_, newValue) => {
                          if (newValue) addList(productId, newValue.id);
                        }}
                        getOptionLabel={(o) => o.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        noOptionsText={
                          (listsByProduct.get(productId) || []).length === 0
                            ? 'Nenhuma lista vinculada a este produto'
                            : 'Todas as listas deste produto já foram adicionadas'
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" margin="dense" label="Adicionar lista" />
                        )}
                      />
                    </span>
                  </Tooltip>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Empresas.'}>
        <span style={{ display: 'block', width: '100%' }}>
          <Autocomplete<Product, false, false, false>
            options={productsNotInMap}
            value={null}
            blurOnSelect
            disabled={!canManage || submitting}
            fullWidth
            sx={{ maxWidth: 400 }}
            onChange={(_, newValue) => {
              if (newValue) addProduct(newValue.id);
            }}
            getOptionLabel={(o) => o.nomeComercial || o.titulo}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            noOptionsText="Todos os produtos já foram adicionados"
            renderInput={(params) => (
              <TextField {...params} size="small" label="Adicionar produto" placeholder="Buscar produto" />
            )}
          />
        </span>
      </Tooltip>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyListsPage;
